import { resolveIdLink, type IdLink } from "@/utils/idLink";
import { Link } from "react-router";
import {
  RecordCellMarker,
  SubjectCellMarker,
} from "@/components/UI/DiscrepancyMarker";
import { DISCREPANCY_COLUMN } from "@/components/Templates/discrepancyMarkerColumn";
import { toTitleCase } from "./ActivitiesTableUtilFunctions";
import {
  COLUMNS_WITH_LINK,
  PILL_APPLIED_COLUMNS,
  NUMERIC_COLUMNS,
  COLUMN_WIDTHS,
  TITLE_MAPPING,
} from "./OverviewConstants";
import {
  iconTypeMap,
  tooltipTitle,
  pillColor,
  textColor,
} from "./VAStatusMapping";
import StatusPill from "../../components/UI/StatusPill";
import { Typography } from "antd";
const { Paragraph } = Typography;
export const getFormattedTitles = (title: string): string => {
  const newTitle = toTitleCase(title);
  if (TITLE_MAPPING[title]) return TITLE_MAPPING[title];
  return newTitle;
};

export const getCellWidth = (header: string): number | string => {
  return COLUMN_WIDTHS[header] || COLUMN_WIDTHS.DEFAULT;
};

// ── Data Mastery queue column styling ─────────────────────────────────────────

const MASTERY_PRIORITY_COLORS: Record<string, { bg: string; text: string }> = {
  high: { bg: "#FEE2E2", text: "#BE0303" },
  medium: { bg: "#FEF3C7", text: "#B45309" },
  low: { bg: "#E8EEF2", text: "#556C75" },
};

const MASTERY_SOURCE_LABELS: Record<string, string> = {
  conflict: "Conflict",
  pair_review: "Pair review",
  pre_mastering: "Pre-mastering",
  pi_review: "PI dedup",
};

const confidenceColor = (pct: number): string =>
  pct >= 80 ? "#22C55E" : pct >= 60 ? "#F59E0B" : "#EF4444";

const renderMasteryCell = (value: unknown, header: string): React.ReactNode => {
  if (header === "priority" && typeof value === "string") {
    const c = MASTERY_PRIORITY_COLORS[value.toLowerCase()];
    if (!c) return undefined;
    return (
      <span
        className="inline-block text-[11px] font-medium rounded px-1.5 py-0.5 capitalize"
        style={{ backgroundColor: c.bg, color: c.text }}
      >
        {value}
      </span>
    );
  }
  if (header === "item_source" && typeof value === "string") {
    const label = MASTERY_SOURCE_LABELS[value];
    if (!label) return undefined;
    return (
      <span className="inline-block text-[11px] font-medium rounded px-1.5 py-0.5 bg-[#E8EEF2] text-[#48636F]">
        {label}
      </span>
    );
  }
  if (header === "confidence_pct" && typeof value === "number") {
    return (
      <span
        className="text-[12px] font-semibold"
        style={{ color: confidenceColor(value) }}
      >
        {Math.round(value)}%
      </span>
    );
  }
  if (header === "weighted_score" && typeof value === "number") {
    return <span className="text-[12px] tabular-nums">{value.toFixed(2)}</span>;
  }
  return undefined;
};

// ── Site delay-risk scoring columns ───────────────────────────────────────────
// Deliberately no fallback band for an unrecognised classification: a site the
// scorer withheld a score from must read as missing data, never as low risk.

const RISK_BAND_COLORS: Record<string, { bg: string; text: string }> = {
  high: { bg: "#FEE2E2", text: "#BE0303" },
  medium: { bg: "#FEF3C7", text: "#B45309" },
  low: { bg: "#DCFCE7", text: "#15803D" },
};

const renderRiskCell = (value: unknown, header: string): React.ReactNode => {
  if (header === "risk_classification" && typeof value === "string") {
    const band = RISK_BAND_COLORS[value.toLowerCase()];
    if (!band) return undefined;
    return (
      <span
        className="inline-block text-[11px] font-medium rounded px-1.5 py-0.5"
        style={{ backgroundColor: band.bg, color: band.text }}
      >
        {value}
      </span>
    );
  }
  // 0-1 scores render to two decimals, matching the mastering-confidence
  // convention rather than being dressed up as percentages.
  if (
    (header === "risk_score" ||
      header === "risk_confidence" ||
      header === "historical_delay_rate") &&
    typeof value === "number"
  ) {
    return <span className="text-[12px] tabular-nums">{value.toFixed(2)}</span>;
  }
  if (header === "avg_milestone_days" && typeof value === "number") {
    return (
      <span className="text-[12px] tabular-nums">{Math.round(value)} d</span>
    );
  }
  return undefined;
};

export const getCellRenderer = (
  value: unknown,
  header: string,
  path: string | ((value: unknown) => string),
  type: string,
  selectionIdentifier: string,
  rowIsSubject = false,
  record?: Record<string, unknown>,
) => {
  // Shared with the plot drill-down so a linked cell and a linked datapoint on
  // the same screen can never resolve the same identifier differently.
  const pathTo = resolveIdLink(
    path as IdLink,
    value == null ? "" : String(value),
  );

  // The discrepancy column is synthetic: it has no backing value, so it must be
  // handled before the null guard below.
  if (header === DISCREPANCY_COLUMN) {
    return rowIsSubject ? (
      <SubjectCellMarker subjectId={record?.subject as string} />
    ) : (
      <RecordCellMarker record={record} />
    );
  }

  if (value == null) return "-";

  if (typeof value === "boolean") return value ? "Yes" : "No";

  // Special Formatting for IP Administration screens
  if (
    header === "dose_per_weight" ||
    header === "dose_mg_edc" ||
    header === "new_actual_dose" ||
    header === "new_actual_dose_per_weight" ||
    header === "modified_actual_dose_due_to_weight"
  ) {
    return (value as number).toFixed(3);
  }

  if (header === "weight") {
    return (value as number).toFixed(2);
  }

  // Data Mastery queue columns (priority pill, source chip, confidence %)
  const masteryCell = renderMasteryCell(value, header);
  if (masteryCell !== undefined) return masteryCell;

  // Site delay-risk columns (band chip, 0-1 scores, milestone latency)
  const riskCell = renderRiskCell(value, header);
  if (riskCell !== undefined) return riskCell;

  if (PILL_APPLIED_COLUMNS.has(header) || !header) {
    return (
      <StatusPill
        text={value as string | number}
        tooltipTitle={tooltipTitle}
        pillColor={pillColor}
        textColor={textColor}
      />
    );
  }

  if (
    (COLUMNS_WITH_LINK.has(header) ||
      (selectionIdentifier && header === selectionIdentifier && path)) &&
    pathTo
  ) {
    return (
      <Link
        style={{ color: "#06709B" }}
        data-testid={
          header === "subject" ? `table-subject-link-${value}` : undefined
        }
        to={pathTo}
      >
        {value as string}
      </Link>
    );
  }
  // TIMEPOINTS: Check if the text is one of the keys in the iconTypeMap
  if (type === "completion" && Object.hasOwn(iconTypeMap, value as string)) {
    return iconTypeMap[value as string];
  }

  if ((value as string)?.length > 10) {
    return (
      <Paragraph ellipsis={{ rows: 3, tooltip: value as string }}>
        {value as string}
      </Paragraph>
    );
  }
  return value;
};

export const getSorter = (header: string) => {
  return (a: Record<string, unknown>, b: Record<string, unknown>) => {
    // String columns without timepoints
    if (!NUMERIC_COLUMNS.has(header)) {
      return (a[header] as string).localeCompare(b[header] as string);
    }

    // Numeric columns
    if (NUMERIC_COLUMNS.has(header)) {
      const aNum = parseFloat(a[header] as string);
      const bNum = parseFloat(b[header] as string);
      return aNum - bNum;
    }

    // Default case: handle both strings and numbers
    if (typeof a[header] === "string" && typeof b[header] === "string") {
      return (a[header] as string).localeCompare(b[header] as string);
    }
    return (a[header] as number) - (b[header] as number);
  };
};

export const createFilters = (
  rows: Record<string, unknown>[],
  header: string,
) => {
  const columnValues = new Set(
    rows.map((row) => (row[header] === "-" ? "Not Required" : row[header])),
  );
  return Array.from(columnValues)
    .map((value) => {
      return { text: value, value };
    })
    .sort((a, b) => {
      const aText = (a.text as unknown as { toString(): string }).toString();
      const bText = (b.text as unknown as { toString(): string }).toString();

      // Check if both values are numeric
      const aIsNumeric = !isNaN(aText as unknown as number);
      const bIsNumeric = !isNaN(bText as unknown as number);

      if (aIsNumeric && bIsNumeric) {
        return parseFloat(aText) - parseFloat(bText);
      }

      return aText.localeCompare(bText);
    });
};
